import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import IconifyIcon from "../../IconifyIcon";
import { User } from "../../../api/models/user-model";
import { selectUserRole, setUser } from "../../../slices/user";
import { showError } from "../../../utils/helpers";
import api, { CreateUserPayload } from "../../../api";

interface ManageAccountModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  user: User | null;
}

const ManageAccountModal = ({
  showModal,
  setShowModal,
  user,
}: ManageAccountModalProps) => {
  const labels = useAppSelector(selectLabels);
  const userRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();

  const userCountryCode = user?.phone
    ? user?.phone.startsWith("+1")
      ? "+1"
      : "+52"
    : "";
  const userCurrentPhone = user?.phone
    ? user?.phone.startsWith("+1")
      ? user?.phone.slice(2)
      : user?.phone.slice(3)
    : "";

  const [editUser, setEditUser] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>(user?.name ? user.name : "");
  const [userEmail, setUserEmail] = useState<string>(
    user?.email ? user.email : ""
  );
  const [countryCode, setCountryCode] = useState<"+52" | "+1">(
    userCountryCode ? userCountryCode : "+52"
  );
  const [userPhone, setUserPhone] = useState<string>(
    userCurrentPhone ? userCurrentPhone : ""
  );

  const countryCodeArray = [
    {
      label: "MX +52",
      value: "+52",
    },
    {
      label: "US +1",
      value: "+1",
    },
  ];

  const updateUser = async () => {
    if (user) {
      let currentUser = { ...user };
      try {
        let payload: CreateUserPayload = {
          username: "",
          name: "",
          phone: "",
          email: "",
          unit: undefined,
        };
        if (userRole === "host") {
          payload = {
            username: userName,
            name: userName,
            phone: `${countryCode}${userPhone}`,
            email: userEmail,
          };
        }
        if (userRole === "secondary_host") {
          payload = {
            username: userName,
            name: userName,
            phone: `${countryCode}${userPhone}`,
            email: userEmail,
          };
        }
        if (userRole === "guard") {
          payload = {
            username: userName,
            name: userName,
            email: userEmail,
          };
        }
        if (userRole === "admin") {
          payload = {
            username: userName,
            name: userName,
            email: userEmail,
          };
        }
        const response = await api.updateUser(user?.id, payload);
        if (response) {
          currentUser = {
            ...currentUser,
            name: userName,
            username: userName,
            email: userEmail,
          };
          if (user?.phone) {
            currentUser = {
              ...currentUser,
              phone: `${countryCode}${userPhone}`,
            };
          }
          dispatch(setUser(currentUser));
          setEditUser(false);
        }
      } catch (error: any) {
        console.error(error);
        if (
          error?.response?.data?.error?.message ===
          "email must be a valid email"
        ) {
          showError(dispatch, labels.invalid_email);
        } else if (
          error?.response?.data?.error?.message ===
          "Email or Username are already taken"
        ) {
          showError(dispatch, labels.existed_user);
        } else if (
          error?.response?.data?.error?.message === "Email already taken"
        ) {
          showError(dispatch, labels.taken_email);
        } else {
          showError(dispatch, labels.generic_error);
        }
      }
    }
  };

  return (
    <Modal open={showModal} onClose={() => setShowModal(false)}>
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          style={{
            maxWidth: 500,
            minWidth: 350,
            width: "90%",
            boxSizing: "border-box",
            paddingTop: 20,
            paddingBottom: 20,
            paddingRight: 30,
            paddingLeft: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div className="w-100 border-box flex align-items-center justify-content-between">
            <p>{labels.account_management}</p>
            <IconifyIcon
              icon={"material-symbols:close-rounded"}
              style={{ fontSize: 24 }}
              className="pointer"
              onClick={() => {
                setShowModal(false);
                setEditUser(false);
              }}
            />
          </div>
          <Divider className="w-100 mt-5 mb-6" />
          {user && (
            <div className="w-100 border-box">
              <p className="mt-4 body-text-sm mb-2">
                {labels.user}:{" "}
                <span className="body-text-md font-w-500">{user?.name}</span>
              </p>
              {userRole === "guard" && (
                <p className="mt-2 body-text-sm mb-2 font-w-500">
                  {labels.security_guard} {labels.of} {user?.property?.name}
                </p>
              )}
              {userRole === "admin" && (
                <p className="mt-2 body-text-sm mb-2 font-w-500">
                  {labels.property_manager} {labels.of} {user?.property?.name}
                </p>
              )}
              {userRole === "host" && (
                <p className="mt-2 body-text-sm mb-2 font-w-500">
                  {labels.main_user} {labels.of} {labels.the_unit}{" "}
                  {user?.unit?.name}
                </p>
              )}
              {userRole === "secondary_host" && (
                <p className="mt-2 body-text-sm mb-2 font-w-500">
                  {labels.secondary_user} {labels.of} {labels.the_unit}{" "}
                  {user?.unit?.name}
                </p>
              )}
              {user?.email && (
                <p className="mt-8 body-text-sm mb-2">
                  {labels.email}:{" "}
                  <span className="body-text-md font-w-500">{user?.email}</span>
                </p>
              )}
              {user?.phone && (
                <p className="mt-8 body-text-sm mb-2">
                  {labels.telephone}:{" "}
                  <span className="body-text-md font-w-500">{user?.phone}</span>
                </p>
              )}
              {userRole === "guard" && (
                <>
                  <p className="mt-8 body-text-sm mb-2">
                    {labels.allowed_access_points}:
                  </p>
                  {user?.access_points &&
                    user?.access_points.map((item, index) => (
                      <p
                        key={`${item?.id}-${index}`}
                        className="body-text-md font-w-500"
                      >
                        {index + 1}.- {item?.name}
                      </p>
                    ))}
                </>
              )}
              {!editUser && (
                <div className="w-100 border-box flex justify-content-end">
                  <Button onClick={() => setEditUser(true)}>
                    {labels.edit}
                  </Button>
                </div>
              )}
              {editUser && (
                <>
                  <Divider className="w-100 mt-10" />
                  <div className="w-100 border-box mb-6 p-4" style={{ backgroundColor: "#f0f0f0" }}>
                    <p className="body-text-xs uppercase font-w-500">{labels.edit_user}</p>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                      <p className="body-text-xs mb-2">{labels.user_name}</p>
                      <TextField
                        fullWidth
                        size="small"
                        value={userName}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setUserName(event.target.value);
                        }}
                      />
                    </Grid>
                    {user?.email && (
                      <Grid item xs={12} sm={12} md={user?.phone ? 6 : 12}>
                        <p className="body-text-xs mb-2">{labels.email}</p>
                        <TextField
                          fullWidth
                          size="small"
                          value={userEmail}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setUserEmail(event.target.value);
                          }}
                        />
                      </Grid>
                    )}
                    {user?.phone && (
                      <Grid item xs={12} sm={12} md={user?.email ? 6 : 12}>
                        <p className="body-text-xs mb-2">{labels.telephone}</p>
                        <div className="w-100 border-box flex align-items-center gap-2">
                          <FormControl style={{ width: 150 }}>
                            <Select
                              value={countryCode}
                              onChange={(event: SelectChangeEvent) =>
                                setCountryCode(
                                  event.target.value as "+52" | "+1"
                                )
                              }
                              size="small"
                            >
                              {countryCodeArray.map((item, index) => (
                                <MenuItem
                                  key={`${index}-${item?.label}`}
                                  value={item?.value}
                                >
                                  {item?.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            size="small"
                            value={userPhone}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setUserPhone(event.target.value);
                            }}
                          />
                        </div>
                      </Grid>
                    )}
                  </Grid>
                  <div className="w-100 border-box flex align-items-center justify-content-end gap-4 mt-8">
                    <Button onClick={() => setEditUser(false)}>
                      {labels.cancel}
                    </Button>
                    <Button onClick={updateUser}>{labels.save_changes}</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Paper>
      </Box>
    </Modal>
  );
};

export default ManageAccountModal;
